import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ErrorHandlerService } from '../../core/services/error-handler.service';
import { PasswordResetService } from '../../services';

import * as fromActions from './actions';
import { effectHooks } from '@app/helpers';

@Injectable()
export class PasswordResetStoreEffects {
  constructor(
    private dataService: PasswordResetService,
    private actions$: Actions,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    private modalService: NzModalService,
    private translate: TranslateService,
  ) {}

  createPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createPasswordResetRequest),
      switchMap(({ email, hooks }) =>
        this.dataService.createPasswordReset(email).pipe(
          effectHooks(hooks),
          map(() => fromActions.createPasswordResetSuccess()),
          catchError(() =>
            of(fromActions.createPasswordResetFailure({ error: null })),
          ),
        ),
      ),
    ),
  );

  checkToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.checkTokenRequest),
      switchMap(({ token, email }) =>
        this.dataService.checkToken(token, email).pipe(
          map(() => {
            return fromActions.checkTokenSuccess();
          }),
          catchError((error) => {
            this.errorHandler.handle(error);
            this.router.navigate(['/login']);
            return of(fromActions.checkTokenFailure({ error: null }));
          }),
        ),
      ),
    ),
  );

  confirmPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.confirmPasswordResetRequest),
      switchMap(({ password, token, email, hooks }) =>
        this.dataService.confirmPasswordReset(token, password, email).pipe(
          effectHooks(hooks),
          map(() => {
            this.modalService.success({
              nzTitle: this.translate.instant('done'),
              nzContent: this.translate.instant(
                'messagere_reset_password_complete_email',
              ),
            });
            this.router.navigate(['/login']);
            return fromActions.confirmPasswordResetSuccess();
          }),
          catchError((error) => {
            if (error.status !== 422) {
              this.errorHandler.handle(error);
              this.router.navigate(['/login']);
            }

            return of(
              fromActions.confirmPasswordResetFailure({
                error: this.getConfirmPasswordResetFailureError(error),
              }),
            );
          }),
        ),
      ),
    ),
  );

  private getConfirmPasswordResetFailureError(error: any): string | null {
    if (error?.status !== 422) {
      return null;
    }

    const message = error.error?.errors?.password?.[0] ?? error.error?.message;

    return typeof message === 'string' ? message : null;
  }
}
